'use client';

import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import IconTriangle from 'app/components/sprites/IconTriangle';
import { IconPause } from 'app/components/sprites';
import { IAudio } from 'app/interfaces/blocks';
import {
  defaultProcessText,
  sanitizeEmbed
} from '@/app/utility/textProcessing';

interface IProps {
  audio: IAudio;
  className?: string;
}

export default function AudioPlayer({ audio, className }: IProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const audioElement = audioRef.current;

    if (!audioElement) return;

    const setAudioData = () => {
      setDuration(audioElement.duration || 0);
    };

    const setAudioTime = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setHasError(true);
      setIsPlaying(false);
    };

    if (audioElement.readyState >= 1) {
      setAudioData();
    } else {
      audioElement.addEventListener('loadedmetadata', setAudioData);
    }

    audioElement.addEventListener('timeupdate', setAudioTime);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('error', handleError);

    return () => {
      audioElement.removeEventListener('loadedmetadata', setAudioData);
      audioElement.removeEventListener('timeupdate', setAudioTime);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current || hasError) return;
    setIsAnimating(true);

    setTimeout(() => {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play().catch(error => {
          console.error('Audio playback error:', error);
          setHasError(true);
        });
      }
      setIsPlaying(!isPlaying);
      setIsAnimating(false);
    }, 300);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (audio.type === 'embed' && audio.embed) {
    return (
      <div className={className}>
        <div className="bg-sand-dark flex flex-col gap-6 md:gap-8 p-2 md:p-5 rounded w-full">
          {!!audio.name && (
            <div
              className="text-p5"
              dangerouslySetInnerHTML={{
                __html: defaultProcessText(audio.name)
              }}
            ></div>
          )}
          <div
            className="w-full [&_iframe]:w-full"
            dangerouslySetInnerHTML={{ __html: sanitizeEmbed(audio.embed) }}
          />
        </div>
      </div>
    );
  }

  if (audio.type === 'file' && audio.file?.url) {
    return (
      <div
        className={classNames(
          'bg-sand-dark flex flex-col gap-4 p-2 md:p-5 rounded w-full',
          className
        )}
      >
        <audio
          ref={audioRef}
          src={audio.file.url}
          preload="metadata"
          className="hidden"
        />

        {!!audio.name && (
          <div
            className="text-p5"
            dangerouslySetInnerHTML={{ __html: defaultProcessText(audio.name) }}
          ></div>
        )}

        <div className="flex justify-between items-center gap-3 md:gap-8">
          <div className="w-full flex flex-col gap-2">
            <div className="flex justify-between">
              <div className="text-sm">{formatTime(currentTime)}</div>
              <div className="text-sm">{formatTime(duration)}</div>
            </div>

            <div className="relative h-1">
              <div className="absolute inset-0 rounded-full bg-white-40" />
              <div
                className="absolute h-full rounded-full bg-brown"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={togglePlay}
              className="relative group"
              aria-label={isPlaying ? 'Пауза' : 'Воспроизведение'}
              disabled={isAnimating || hasError}
            >
              <div className="absolute inset-0 bbg-transparent rounded-full blur-md opacity-50 group-hover:opacity-70 transition-opacity" />

              <div className="relative flex justify-center items-center w-12 h-12 xl:w-15 xl:h-15 bg-white rounded-lg group-hover:scale-105 transition-transform duration-300">
                <IconTriangle
                  className={classNames(
                    'w-6 h-6 fill-brown ml-1 absolute transition-all duration-300 ease-in-out',
                    {
                      'opacity-0 scale-50 -translate-x-4': isPlaying,
                      'opacity-100 scale-100 translate-x-0':
                        !isPlaying && !isAnimating,
                      'opacity-0': isAnimating
                    }
                  )}
                />

                <IconPause
                  className={classNames(
                    'w-15 h-15 stroke-brown absolute transition-all duration-300 ease-in-out',
                    {
                      'opacity-0 scale-50 translate-x-4': !isPlaying,
                      'opacity-100 scale-100 translate-x-0':
                        isPlaying && !isAnimating,
                      'opacity-0': isAnimating
                    }
                  )}
                />
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Фолбэк: если данные аудио несогласованы (type/embed/file не совпали ни с
  // одной веткой), не возвращаем undefined — иначе React 18 бросает
  // «Nothing was returned from render».
  return null;
}
